'use client';

import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger';

import { SUPPORTED_FEATURE_FLAGS } from '@/utils/featureFlags';

/**
 * Resolves a flag from the URL query and cookie, persisting the toggle the
 * same way `useFeature` does. Returns `undefined` when there is no explicit
 * signal or outside the browser.
 */
export function resolveFlagFromQueryAndCookie(flag: string): boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  const { search } = window.location;
  const enableFeature = search?.includes(`${flag}=true`);
  const disableFeature = search?.includes(`${flag}=false`);
  const toggleOnPersisted = Cookies.get(flag) === 'true';

  if (disableFeature) {
    Cookies.remove(flag);
    return false;
  }
  if (enableFeature || toggleOnPersisted) {
    Cookies.set(flag, 'true', { expires: 14 });
    return true;
  }
  return undefined;
}

/** Hook to determine feature availability based on the URL query and cookie */
export function useFeature(flag: string) {
  const [featureEnabled, setFeatureEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!SUPPORTED_FEATURE_FLAGS.includes(flag)) {
      logger.warn(`Unsupported feature flag: "${flag}"`);
      return;
    }

    const resolved = resolveFlagFromQueryAndCookie(flag);
    if (resolved != null) {
      setFeatureEnabled(resolved);
    }
  }, [flag]);

  return featureEnabled;
}

/**
 * Enabled feature flags list.
 * @returns Feature flags names.
 */
export const useEnabledFeatures = () => {
  const [flags, setFlags] = useState<string[]>([]);

  useEffect(() => {
    setFlags(SUPPORTED_FEATURE_FLAGS.filter((feature) => Cookies.get(feature) === 'true'));
  }, []);

  return flags;
};

/**
 * Reset all feature flags.
 */
export const resetAllFeatureFlags = () =>
  SUPPORTED_FEATURE_FLAGS.forEach((feature) => {
    Cookies.remove(feature);
  });
